import { useEffect, useState } from 'react';
import type { BrancheResponse } from '@swg/contracts';
import { confirmBranche, fetchBranche, setBrancheSbi } from '../api.js';
import { theme } from '../theme.js';

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'medium' }).format(new Date(iso));
}

function formatNumber(value: number, unit: string | null): string {
  const formatted = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 }).format(value);
  if (!unit || unit === 'aantal') return formatted;
  if (unit === '%') return `${value > 0 ? '+' : ''}${formatted}%`;
  return `${formatted} ${unit}`;
}

/** Toont een SBI-code leesbaar: "2562" → "25.62". */
function formatSbi(code: string): string {
  if (!/^\d{3,}$/.test(code)) return code;
  return `${code.slice(0, 2)}.${code.slice(2)}`;
}

const SBI_SOURCE_LABELS: Record<string, { label: string; badge: string; bg: string; fg: string }> = {
  KVK: { label: 'bron: KVK (officiële registratie)', badge: 'FEIT', bg: '#E4F3EB', fg: theme.color.ok },
  AI_SUGGESTED: { label: 'bron: AI-suggestie (OpenAI) o.b.v. profiel', badge: 'AANNAME', bg: theme.color.warnBg, fg: theme.color.warnText },
  MANUAL: { label: 'bron: handmatig gekozen', badge: 'GECORRIGEERD', bg: '#E4F3EB', fg: theme.color.ok },
};

const cardStyle = {
  background: theme.color.surface,
  border: `1px solid ${theme.color.border}`,
  borderRadius: 12,
  padding: '16px 18px',
} as const;

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  border: `1px solid ${theme.color.borderStrong}`,
  borderRadius: 8,
  padding: '9px 12px',
  fontSize: 14,
  fontFamily: 'inherit',
  color: theme.color.text,
  background: theme.color.surface,
} as const;

function badge(bg: string, fg: string, text: string) {
  return (
    <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '0.05em', background: bg, color: fg, borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap' }}>
      {text}
    </span>
  );
}

/**
 * Stap "Branche" (C09): toont de vastgestelde SBI-code (met herkomst en
 * zekerheid) en de CBS-branchestatistieken. Ontbrekende waarden blijven
 * zichtbaar als "niet beschikbaar" — nooit 0. Handmatige SBI-correctie heeft
 * voorrang en haalt de cijfers opnieuw op.
 */
export function BrancheInsight({
  runId,
  runActive,
  canEdit,
  onConfirmed,
  onBack,
}: {
  runId: string;
  runActive: boolean;
  canEdit: boolean;
  onConfirmed: () => void;
  onBack: () => void;
}) {
  const [data, setData] = useState<BrancheResponse | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingSbi, setEditingSbi] = useState(false);
  const [sbiDraft, setSbiDraft] = useState('');

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const res = await fetchBranche(runId);
        if (cancelled) return;
        setData(res);
        setLoaded(true);
      } catch {
        if (!cancelled) setLoaded(true);
      }
      if (!cancelled && runActive) timer = setTimeout(tick, 3000);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [runId, runActive]);

  const atCheckpoint = data?.runStatus === 'BRANCHE_REVIEW_REQUIRED';
  const editable = canEdit && atCheckpoint;

  const onSaveSbi = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await setBrancheSbi(runId, sbiDraft.trim());
      setEditingSbi(false);
      onConfirmed(); // status is terug naar BRANCHE_QUEUED → volg de run opnieuw
    } catch (e) {
      setError(e instanceof Error ? e.message : 'De SBI-code kon niet worden opgeslagen.');
    } finally {
      setBusy(false);
    }
  };

  const onConfirm = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await confirmBranche(runId);
      onConfirmed();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Bevestigen is mislukt.');
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return <p style={{ color: theme.color.muted, fontSize: 13.5 }}>Laden…</p>;

  if (runActive || !data?.available) {
    return (
      <div style={{ maxWidth: 560, margin: '40px auto 0', textAlign: 'center' }}>
        <div
          aria-label="Laden"
          style={{
            width: 44,
            height: 44,
            border: `4px solid ${theme.color.border}`,
            borderTopColor: theme.color.primary,
            borderRadius: '50%',
            margin: '0 auto 22px',
            animation: 'swg-spin 0.9s linear infinite',
          }}
        />
        <h2 style={{ margin: '0 0 8px', fontSize: 20, fontWeight: 600 }}>Branche-informatie ophalen</h2>
        <div style={{ fontSize: 14, color: theme.color.muted }}>
          De SBI-code wordt bepaald en de CBS-branchestatistieken worden opgehaald.
        </div>
      </div>
    );
  }

  const sbi = data.sbi;
  const sourceMeta = sbi ? SBI_SOURCE_LABELS[sbi.source] : null;

  return (
    <div>
      <h1 style={{ margin: '0 0 6px', fontSize: 23, fontWeight: 700, letterSpacing: '-0.01em' }}>
        Branche-inzicht
      </h1>
      <p style={{ margin: '0 0 18px', color: theme.color.muted, fontSize: 14 }}>
        Sectorcontext uit open data van het CBS · een fout bij deze bron laat de analyse niet
        mislukken.
      </p>

      {data.failureSummary && (
        <div
          style={{
            background: theme.color.warnBg,
            border: `1px solid ${theme.color.warnBorder}`,
            borderRadius: 10,
            padding: '12px 16px',
            fontSize: 13.5,
            color: '#6E5410',
            marginBottom: 14,
            lineHeight: 1.5,
          }}
        >
          {data.failureSummary}
        </div>
      )}

      {/* SBI-kaart */}
      <div style={{ ...cardStyle, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 7 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              SBI-code (branche)
            </span>
            {sourceMeta && badge(sourceMeta.bg, sourceMeta.fg, sourceMeta.badge)}
          </div>
          {editable && !editingSbi && (
            <button
              onClick={() => {
                setSbiDraft(sbi ? formatSbi(sbi.code) : '');
                setEditingSbi(true);
              }}
              style={{ background: 'none', border: 'none', color: theme.color.primary, fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}
            >
              {sbi ? 'Corrigeren' : 'Handmatig kiezen'}
            </button>
          )}
        </div>

        {editingSbi ? (
          <div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                style={{ ...inputStyle, maxWidth: 160 }}
                placeholder="bijv. 25.62"
                value={sbiDraft}
                onChange={(e) => setSbiDraft(e.target.value)}
              />
              <button
                onClick={() => void onSaveSbi()}
                disabled={busy}
                style={{ background: theme.color.primary, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit' }}
              >
                {busy ? 'Bezig…' : 'Opslaan en opnieuw ophalen'}
              </button>
              <button
                onClick={() => setEditingSbi(false)}
                disabled={busy}
                style={{ background: 'transparent', color: theme.color.text, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
              >
                Annuleren
              </button>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, color: theme.color.muted }}>
              2–4 cijfers volgens SBI 2008 (bijv. 25.62 voor algemene metaalbewerking).
            </div>
          </div>
        ) : sbi ? (
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>
              <span style={{ fontFamily: theme.font.mono }}>{formatSbi(sbi.code)}</span>
              {sbi.title && <span style={{ fontWeight: 400 }}> · {sbi.title}</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 11.5, color: theme.color.muted, flexWrap: 'wrap' }}>
              {sourceMeta && <span>{sourceMeta.label}</span>}
              {sbi.determinedAt && <span>· bepaald {formatDate(sbi.determinedAt)}</span>}
              {sbi.rationale && sbi.source === 'AI_SUGGESTED' && <span>· {sbi.rationale}</span>}
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 14, color: theme.color.muted, lineHeight: 1.5 }}>
            Er is geen SBI-code bepaald. Vul een KVK-nummer in bij het bedrijfsprofiel of kies de
            branche hier handmatig — zonder SBI-code blijven de branchecijfers leeg.
          </div>
        )}
      </div>

      {/* Nichegrootte (P21/P22) — naast het CBS-cijfer, met het verschil benoemd.
          Bewust dezelfde grid-plek als een statistiekkaart maar een eigen rand:
          het is een ander soort getal. De CBS-telling gaat over de hele SBI, dit
          over de bedrijven die aantoonbaar hetzelfde doen. */}
      {data.nicheFloor && (
        <div
          style={{
            ...cardStyle,
            marginBottom: 14,
            borderColor: theme.color.borderStrong,
            borderLeft: `3px solid ${theme.color.primary}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Bedrijven in de niche
            </span>
            {badge(
              theme.color.accentBg,
              theme.color.primary,
              data.nicheFloor.status === 'PENDING' ? 'NOG NIET BEPAALD' : 'ONDERGRENS',
            )}
          </div>
          {data.nicheFloor.status === 'DETERMINED' && data.nicheFloor.verifiedCompetitors !== null ? (
            <div style={{ fontSize: 22, fontWeight: 700, color: theme.color.text }}>
              ≥ {data.nicheFloor.verifiedCompetitors}
              {/* P25 — de breuk waar de feedback om vroeg: hoe groot is de niche
                  binnen deze SBI. Twee echte getallen naast elkaar, geen
                  percentage: de teller is een ondergrens en de noemer een
                  telling, en een percentage zou die twee gelijkwaardig maken. */}
              {data.nicheFloor.sbiCompanies !== null && data.nicheFloor.sbiCompanies > 0 && (
                <span style={{ fontSize: 14, fontWeight: 400, color: theme.color.muted }}>
                  {' '}van {new Intl.NumberFormat('nl-NL').format(data.nicheFloor.sbiCompanies)}
                </span>
              )}
              <span style={{ fontSize: 12.5, fontWeight: 400, color: theme.color.muted }}> bedrijven</span>
            </div>
          ) : (
            // Bewust geen getal en zeker geen 0: er is nog niet gekeken. Een 0
            // hier zou zeggen dat we hebben gezocht en niemand hebben gevonden.
            <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.subtle }}>
              Volgt na de concurrentstap
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 12, color: theme.color.muted, lineHeight: 1.6 }}>
            {data.nicheFloor.labelNl}
          </div>
          {data.nicheFloor.status === 'DETERMINED' && (
          <div style={{ marginTop: 6, fontSize: 11.5, color: theme.color.subtle, lineHeight: 1.6 }}>
            Het CBS-cijfer hieronder telt álle bedrijven met deze SBI-code. Dat is een bredere groep:
            dezelfde code dekt behandelingen die dit bedrijf niet levert. Dit getal telt alleen
            bedrijven waarvan op hun eigen bron is teruggevonden dat ze minstens één van de eigen
            processen leveren — geen schatting, en dus per definitie te laag.
          </div>
          )}
        </div>
      )}

      {/* Statistieken */}
      {data.statistics.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
          {data.statistics.map((s) => {
            const available = s.availability === 'AVAILABLE' && s.valueNumber !== null;
            // `matchLevel` komt van de server (P11). De vorige lokale
            // tekstvergelijking zag "25.62" en "2562" als verschillend en
            // waarschuwde dus voor een terugval die er niet was.
            const coarser = s.matchLevel === 'COARSER' || s.matchLevel === 'UNRELATED';
            return (
              <div key={s.metricCode} style={cardStyle}>
                <div style={{ fontSize: 12, fontWeight: 600, color: theme.color.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                  {s.nameNl}
                </div>
                {available ? (
                  <div style={{ fontSize: 22, fontWeight: 700, color: theme.color.text }}>
                    {formatNumber(s.valueNumber!, s.unit)}
                    {s.unit === 'aantal' && (
                      <span style={{ fontSize: 12.5, fontWeight: 400, color: theme.color.muted }}> bedrijven</span>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 15, fontWeight: 600, color: theme.color.subtle }}>
                    Niet beschikbaar
                  </div>
                )}
                <div style={{ marginTop: 8, fontSize: 11.5, color: theme.color.muted, lineHeight: 1.6 }}>
                  {s.periodLabel && <div>periode: {s.periodLabel}</div>}
                  {coarser && s.matchedSbiCode && (
                    <div style={{ color: theme.color.warnText }}>
                      niveau: SBI {formatSbi(s.matchedSbiCode)}
                      {s.matchedSbiTitle ? ` — ${s.matchedSbiTitle}` : ''}
                      {s.matchNote && <div>{s.matchNote}</div>}
                    </div>
                  )}
                  <div>
                    bron: {s.source.name}
                    {s.source.tableId ? ` · tabel ${s.source.tableId}` : ''} · opgehaald{' '}
                    {formatDate(s.source.retrievedAt)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p style={{ marginTop: 14, fontSize: 11.5, color: theme.color.subtle }}>
        Bron: {data.attribution}. Sectorcijfers beschrijven de hele branche, niet individuele
        bedrijven.
      </p>

      {error && (
        <div style={{ background: '#FBE6E6', border: '1px solid #EFC2C2', color: '#A82A2A', borderRadius: 8, padding: '10px 12px', fontSize: 13, marginTop: 14 }}>
          {error}
        </div>
      )}

      {/* Onderste acties */}
      {editable && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 20 }}>
          <button
            onClick={onBack}
            style={{ background: 'transparent', color: theme.color.text, border: `1px solid ${theme.color.borderStrong}`, borderRadius: 8, padding: '9px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            ← Terug
          </button>
          <button
            onClick={() => void onConfirm()}
            disabled={busy}
            style={{ background: theme.color.cta, color: theme.color.ctaText, border: 'none', borderRadius: 8, padding: '11px 20px', fontSize: 14, fontWeight: 700, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1, fontFamily: 'inherit' }}
          >
            {busy ? 'Bezig…' : 'Verder → concurrenten'}
          </button>
        </div>
      )}
    </div>
  );
}
